import React, { useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Formik, FormikErrors, FormikValues } from "formik";
import { ZahtjevContext } from "../../common/context/ZahtjevProvider";
import { useUpdateContactAddress } from "../../api/RegistrationApi";
import {
    ContactAddressFormValues,
    initialContactAddressFormValues,
    CreateZahtjevKontaktAdresaRequest,
    DELIVERY_ADDRESS,
    DeliveryAddress
} from "../../model/ContactAddressType";
import { validateContactAddressField, validateContactAddressForm } from "./contactAddressValidation";
import { ValidateOnMount } from "../common/ValidateOnMount";
import { CONTACT_ADDRESS_STATUS } from "../../common/constants/navigationConstants";
import InputField from "../common/InputField";
import AutocompleteField from "../common/AutocompleteField";
import Switch from "../common/Switch";
import { Button } from "../common/Button";
import { Card } from "../common/Card";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { useNotification } from "../../common/context/NotificationProvider";
import { ERROR_MESSAGE_NOTIFICATION } from "../../common/constants/notificationConstants";
import {
    getCountryDataWithCroatiaBeingFirst,
    getUkrajinaRegijaById,
    getUkrajinaRegijaOptions,
} from "../../util/sifarnikUtil";
import { POZIVNI_BROJ_DATA as POZIVNI_BROJEVI, UKRAJINA_OZNAKA } from "../../common/data/sifarnik";
import { Country } from "../../model/PersonalInformationType";
import { Alert } from "@mui/material";
import { useMediaQuery } from "react-responsive";
import {useTranslation} from "react-i18next";
import {SupportedLanguage} from "../../i18n";
import {Tooltip} from "../common/Tooltip";
import { scrollToFirstError } from "../../util/formUtil";

const POZIVNI_BROJ_DATA = POZIVNI_BROJEVI.map(pozivniBroj => ({
    oznaka: pozivniBroj,
    drzava: pozivniBroj
}));

const findPozivniBrojOption = (value: string) =>
    POZIVNI_BROJ_DATA.find(opt => opt.oznaka === value) || null;

const isUkraine = (country?: Country | null): boolean => country?.oznaka === UKRAJINA_OZNAKA;

const COUNTRY_TO_REGIJA_FIELD: Partial<Record<keyof ContactAddressFormValues, keyof ContactAddressFormValues>> = {
    drzava: "drzavaRegija",
    dostavaDrzava: "dostavaDrzavaRegija",
    boravisteDrzava: "boravisteDrzavaRegija",
    trecaDrzava: "trecaDrzavaRegija",
};

function mapFormValuesToApiRequest(values: ContactAddressFormValues): CreateZahtjevKontaktAdresaRequest {
    return {
        telefonPredbroj: values.telefonPredbroj || null,
        telefon: values.telefon || null,
        mobitelPredbroj: values.mobitelPredbroj,
        mobitel: values.mobitel,
        mailAdresa: values.mailAdresa,
        ulica: values.ulica,
        kucniBr: values.kucniBr,
        mjesto: values.mjesto,
        postanskiBroj: values.postanskiBroj,
        drzava: values.drzava?.oznaka || "",
        drzavaRegija: isUkraine(values.drzava) ? (values.drzavaRegija?.oznaka || null) : null,
        dostavaDrugaAdresa: values.dostavaDrugaAdresa,
        dostavaUlica: values.dostavaDrugaAdresa ? (values.dostavaUlica || null) : null,
        dostavaKucniBr: values.dostavaDrugaAdresa ? (values.dostavaKucniBr || null) : null,
        dostavaMjesto: values.dostavaDrugaAdresa ? (values.dostavaMjesto || null) : null,
        dostavaDrzava: values.dostavaDrugaAdresa ? (values.dostavaDrzava?.oznaka || null) : null,
        dostavaDrzavaRegija: values.dostavaDrugaAdresa && isUkraine(values.dostavaDrzava) ? (values.dostavaDrzavaRegija?.oznaka || null) : null,
        dostavaPostanskiBroj: values.dostavaDrugaAdresa ? (values.dostavaPostanskiBroj || null) : null,
        boraviste: values.boraviste,
        boravisteUlica: values.boraviste ? (values.boravisteUlica || null) : null,
        boravisteKucniBr: values.boraviste ? (values.boravisteKucniBr || null) : null,
        boravisteMjesto: values.boraviste ? (values.boravisteMjesto || null) : null,
        boravisteDrzava: values.boraviste ? (values.boravisteDrzava?.oznaka || null) : null,
        boravisteDrzavaRegija: values.boraviste && isUkraine(values.boravisteDrzava) ? (values.boravisteDrzavaRegija?.oznaka || null) : null,
        boravistePostanskiBroj: values.boraviste ? (values.boravistePostanskiBroj || null) : null,
        trecaAdresa: canHaveTrecaAdresa(values),
        trecaUlica: canHaveTrecaAdresa(values) ? (values.trecaUlica || null) : null,
        trecaKucniBr: canHaveTrecaAdresa(values) ? (values.trecaKucniBr || null) : null,
        trecaMjesto: canHaveTrecaAdresa(values) ? (values.trecaMjesto || null) : null,
        trecaDrzava: canHaveTrecaAdresa(values) ? (values.trecaDrzava?.oznaka || null) : null,
        trecaDrzavaRegija: canHaveTrecaAdresa(values) && isUkraine(values.trecaDrzava) ? (values.trecaDrzavaRegija?.oznaka || null) : null,
        trecaPostanskiBroj: canHaveTrecaAdresa(values) ? (values.trecaPostanskiBroj || null) : null,
        adresaZaDostavuDokumentacije: values.adresaZaDostavuDokumentacije,
    };
}

const canHaveTrecaAdresa = (values: ContactAddressFormValues): boolean =>
    !values.boraviste && values.dostavaDrugaAdresa && values.trecaAdresa;

const ContactAddressPage = () => {
    const { t } = useTranslation();
    const lng = useTranslation().i18n.resolvedLanguage as SupportedLanguage;

    const {
        contactAddressInformation,
        contactAddressCallback,
        getZahtjevId,
        getStatus,
        restoreContext
    } = useContext(ZahtjevContext);

    const { isPending: isLoading, mutate: updateContactAddressMutation } = useUpdateContactAddress(getZahtjevId());
    const currentStatus = getStatus();
    const pageStatus = CONTACT_ADDRESS_STATUS;

    const initialFormValues = Object.keys(contactAddressInformation).length !== 0
        ? contactAddressInformation
        : initialContactAddressFormValues;

    const { notify } = useNotification();
    const navigate = useNavigate();
    const isMobile = useMediaQuery({ maxWidth: 767 });

    const handleSubmit = (values: ContactAddressFormValues, setFieldError: (field: string, message: (string | undefined)) => void) => {
        const isFormValid = validateContactAddressForm(values, setFieldError);

        if (isFormValid) {
            const request = mapFormValuesToApiRequest(values);

            updateContactAddressMutation(request, {
                onSuccess: () => {
                    contactAddressCallback(values);
                    navigate('/financial-information');
                },
                onError: () => {
                    notify(ERROR_MESSAGE_NOTIFICATION, "error");
                }
            });
        } else {
            scrollToFirstError();
        }
    };

    const handleBack = (values: ContactAddressFormValues, dirty: boolean) => {
        if (dirty) contactAddressCallback(values);
        navigate('/personal-information', { state: { isBack: true } });
    };

    const handleValidation = (setFieldError: (field: string, message: (string | undefined)) => void) =>
        (value: any, field?: string) => {
            if (field === 'mobitelPredbroj' || field === 'telefonPredbroj') {
                if (value && typeof value === 'object' && 'oznaka' in value) {
                    return validateContactAddressField(field!, value.oznaka, setFieldError);
                }
                if (typeof value === 'string') {
                    return validateContactAddressField(field!, value, setFieldError);
                }
                return validateContactAddressField(field!, '', setFieldError);
            }
            return validateContactAddressField(field!, value, setFieldError);
        };

    const clearTrecaAdresa = (
        setFieldValue: (field: string, value: any, shouldValidate?: boolean) => Promise<void | FormikErrors<FormikValues>>,
        setFieldError: (field: string, message: (string | undefined)) => void,
    ) => {
        setFieldValue("trecaAdresa", false, false);
        ["trecaUlica", "trecaKucniBr", "trecaMjesto", "trecaPostanskiBroj"].forEach(f => {
            setFieldValue(f, "", false);
            setFieldError(f, undefined);
        });
        setFieldValue("trecaDrzava", null, false);
        setFieldError("trecaDrzava", undefined);
        setFieldValue("trecaDrzavaRegija", undefined, false);
        setFieldError("trecaDrzavaRegija", undefined);
    };

    const handleSetFieldValue = (setFieldValue: (field: string, value: any, shouldValidate?: boolean) => Promise<void | FormikErrors<FormikValues>>,
                                   setFieldError: (field: string, message: (string | undefined)) => void = () => {
                                   },
                                   values?: ContactAddressFormValues) =>
        (value: any, field?: string) => {
            if (field === 'mobitelPredbroj' || field === 'telefonPredbroj') {
                if (value && typeof value === 'object' && 'oznaka' in value) {
                    const stringValue = value.oznaka;
                    validateContactAddressField(field!, stringValue, setFieldError);
                    return setFieldValue(field!, stringValue, false);
                }
                if (typeof value === 'string') {
                    validateContactAddressField(field!, value, setFieldError);
                    return setFieldValue(field!, value, false);
                }
                validateContactAddressField(field!, '', setFieldError);
                return setFieldValue(field!, '', false);
            }

            const resetDeliveryIfSelected = (...targets: DeliveryAddress[]) => {
                if (values && targets.includes(values.adresaZaDostavuDokumentacije)) {
                    setFieldValue("adresaZaDostavuDokumentacije", DELIVERY_ADDRESS.PREBIVALISTE, false);
                }
            };

            if (field === 'dostavaDrugaAdresa' && !(value as boolean)) {
                setFieldValue("dostavaUlica", "", false);
                setFieldError("dostavaUlica", undefined);
                setFieldValue("dostavaKucniBr", "", false);
                setFieldError("dostavaKucniBr", undefined);
                setFieldValue("dostavaMjesto", "", false);
                setFieldError("dostavaMjesto", undefined);
                setFieldValue("dostavaPostanskiBroj", "", false);
                setFieldError("dostavaPostanskiBroj", undefined);
                setFieldValue("dostavaDrzava", null, false);
                setFieldError("dostavaDrzava", undefined);
                setFieldValue("dostavaDrzavaRegija", undefined, false);
                setFieldError("dostavaDrzavaRegija", undefined);
                // The third address can only exist on top of the second one.
                clearTrecaAdresa(setFieldValue, setFieldError);
                resetDeliveryIfSelected(DELIVERY_ADDRESS.DRUGA, DELIVERY_ADDRESS.TRECA);
            } else if (field === 'boraviste') {
                if (!(value as boolean)) {
                    setFieldValue("boravisteUlica", "", false);
                    setFieldError("boravisteUlica", undefined);
                    setFieldValue("boravisteKucniBr", "", false);
                    setFieldError("boravisteKucniBr", undefined);
                    setFieldValue("boravisteMjesto", "", false);
                    setFieldError("boravisteMjesto", undefined);
                    setFieldValue("boravistePostanskiBroj", "", false);
                    setFieldError("boravistePostanskiBroj", undefined);
                    setFieldValue("boravisteDrzava", null, false);
                    setFieldError("boravisteDrzava", undefined);
                    setFieldValue("boravisteDrzavaRegija", undefined, false);
                    setFieldError("boravisteDrzavaRegija", undefined);
                    resetDeliveryIfSelected(DELIVERY_ADDRESS.BORAVISTE);
                } else {
                    // Enabling habitual residence rules out the third address.
                    clearTrecaAdresa(setFieldValue, setFieldError);
                    resetDeliveryIfSelected(DELIVERY_ADDRESS.TRECA);
                }
            }

            const regijaField = COUNTRY_TO_REGIJA_FIELD[field as keyof ContactAddressFormValues];
            if (regijaField && !isUkraine(value as Country | null)) {
                setFieldValue(regijaField, undefined, false);
                setFieldError(regijaField, undefined);
            }

            return setFieldValue(field!, value, false);
        };

    useEffect(() => {
        restoreContext(pageStatus);
    }, []);

    return (
        <Card title={t("contact-address-page.title")} isLoading={parseInt(currentStatus) < parseInt(pageStatus)} bodyClassName="md:w-full">
            <Formik
                initialValues={initialFormValues}
                onSubmit={(values, { setFieldError }) => handleSubmit(values, setFieldError)}
            >{({ values, errors, handleSubmit, setFieldValue, setFieldError, dirty }) => (
                <form noValidate onSubmit={handleSubmit} autoComplete="off">
                    <ValidateOnMount shouldValidate={Object.keys(contactAddressInformation).length !== 0} validate={validateContactAddressForm} />
                    <div className="space-y-5">
                        <Alert className="mb-2 md:w-2/3" sx={{alignItems: 'center'}} severity="info">
                            {t("contact-address-page.required-fields-alert")}
                        </Alert>

                        <div className="!mt-5 flex flex-row">
                            <div className="text-lg font-medium text-gray-700">
                                {t("contact-address-page.permanent-residence-section-title")}
                            </div>
                            <Tooltip text={t("contact-address-page.permanent-residence-info")} className="cursor-help" iconSize="small" />
                        </div>

                        <p className="text-gray-700 mt-5 mb-5">{t("contact-address-page.permanent-residence-declaration")}</p>

                        {isMobile &&
                            <>
                                <InputField
                                    name="ulica"
                                    label={t("contact-address-page.address-field.label")}
                                    maxLength={35}
                                    defaultValue={values.ulica}
                                    error={t(errors?.ulica as string)}
                                    handleValueChange={handleSetFieldValue(setFieldValue)}
                                    handleValidation={handleValidation(setFieldError)}
                                />

                                <InputField
                                    name="kucniBr"
                                    label={t("contact-address-page.house-number-field.label")}
                                    maxLength={16}
                                    defaultValue={values.kucniBr}
                                    error={t(errors?.kucniBr as string)}
                                    handleValueChange={handleSetFieldValue(setFieldValue)}
                                    handleValidation={handleValidation(setFieldError)}
                                />
                            </>
                        }

                        {!isMobile &&
                            <div className="flex flex-row justify-between items-start w-full md:w-2/3">
                                <div className="w-3/4 pr-5">
                                    <InputField
                                        name="ulica"
                                        label={t("contact-address-page.address-field.label")}
                                        maxLength={35}
                                        defaultValue={values.ulica}
                                        error={t(errors?.ulica as string)}
                                        handleValueChange={handleSetFieldValue(setFieldValue)}
                                        handleValidation={handleValidation(setFieldError)}
                                    />
                                </div>

                                <div className="w-3/4 pl-5">
                                    <InputField
                                        name="kucniBr"
                                        label={t("contact-address-page.house-number-field.label")}
                                        maxLength={16}
                                        defaultValue={values.kucniBr}
                                        error={t(errors?.kucniBr as string)}
                                        handleValueChange={handleSetFieldValue(setFieldValue)}
                                        handleValidation={handleValidation(setFieldError)}
                                    />
                                </div>
                            </div>
                        }

                        {isMobile &&
                            <>
                                <InputField
                                    name="mjesto"
                                    label={t("contact-address-page.location-field.label")}
                                    maxLength={35}
                                    defaultValue={values.mjesto}
                                    error={t(errors?.mjesto as string)}
                                    handleValueChange={handleSetFieldValue(setFieldValue)}
                                    handleValidation={handleValidation(setFieldError)}
                                />
                                <InputField
                                    name="postanskiBroj"
                                    label={t("contact-address-page.zip-code-field.label")}
                                    maxLength={16}
                                    defaultValue={values.postanskiBroj}
                                    error={t(errors?.postanskiBroj as string)}
                                    handleValueChange={handleSetFieldValue(setFieldValue)}
                                    handleValidation={handleValidation(setFieldError)}
                                />
                            </>
                        }

                        {!isMobile &&
                            <div className="flex flex-row justify-between items-start w-full md:w-2/3">
                                <div className="w-3/4 pr-5">
                                    <InputField
                                        name="mjesto"
                                        label={t("contact-address-page.location-field.label")}
                                        maxLength={35}
                                        defaultValue={values.mjesto}
                                        error={t(errors?.mjesto as string)}
                                        handleValueChange={handleSetFieldValue(setFieldValue)}
                                        handleValidation={handleValidation(setFieldError)}
                                    />
                                </div>

                                <div className="w-3/4 pl-5">
                                    <InputField
                                        name="postanskiBroj"
                                        label={t("contact-address-page.zip-code-field.label")}
                                        maxLength={16}
                                        defaultValue={values.postanskiBroj}
                                        error={t(errors?.postanskiBroj as string)}
                                        handleValueChange={handleSetFieldValue(setFieldValue)}
                                        handleValidation={handleValidation(setFieldError)}
                                    />
                                </div>
                            </div>
                        }

                        <AutocompleteField
                            className="md:w-2/3"
                            name="drzava"
                            label={t("contact-address-page.country-field.label")}
                            options={getCountryDataWithCroatiaBeingFirst(lng)}
                            getOptionLabel={(option) => option?.drzava || ""}
                            isOptionEqualToValue={(option, value) => option?.oznaka === value?.oznaka}
                            defaultValue={values.drzava}
                            error={t(errors?.drzava as string)}
                            handleValueChange={handleSetFieldValue(setFieldValue)}
                            handleValidation={handleValidation(setFieldError)}
                        />

                        {isUkraine(values.drzava) &&
                            <AutocompleteField
                                className="md:w-2/3"
                                name="drzavaRegija"
                                label={t("common.ukraine-region.label")}
                                options={getUkrajinaRegijaOptions(t)}
                                getOptionLabel={(option) => option?.drzava || ""}
                                isOptionEqualToValue={(option, value) => option?.oznaka === value?.oznaka}
                                defaultValue={values.drzavaRegija?.oznaka
                                    ? getUkrajinaRegijaById(values.drzavaRegija.oznaka, t)
                                    : undefined}
                                error={t(errors?.drzavaRegija as string)}
                                handleValueChange={handleSetFieldValue(setFieldValue)}
                                handleValidation={handleValidation(setFieldError)}
                            />
                        }

                        {!values.trecaAdresa &&
                            <div className="flex flex-col">
                                <Switch
                                    name="boraviste"
                                    label={<span style={{ display: 'inline-flex', alignItems: 'center' }}>{t("contact-address-page.temporary-residence-permit-switch")} <Tooltip text={t("contact-address-page.temporary-residence-permit-tooltip")} className="cursor-help ml-1" iconSize="small" /></span>}
                                    defaultValue={values.boraviste}
                                    handleValueChange={handleSetFieldValue(setFieldValue, setFieldError, values)}
                                />
                            </div>
                        }

                        {values.boraviste &&
                            <>
                                {isMobile &&
                                    <>
                                        <InputField
                                            name="boravisteUlica"
                                            label={t("contact-address-page.address-field.label")}
                                            maxLength={35}
                                            defaultValue={values.boravisteUlica}
                                            error={t(errors.boravisteUlica as string)}
                                            handleValueChange={handleSetFieldValue(setFieldValue)}
                                            handleValidation={handleValidation(setFieldError)}
                                        />

                                        <InputField
                                            name="boravisteKucniBr"
                                            label={t("contact-address-page.house-number-field.label")}
                                            maxLength={16}
                                            defaultValue={values.boravisteKucniBr}
                                            error={t(errors.boravisteKucniBr as string)}
                                            handleValueChange={handleSetFieldValue(setFieldValue)}
                                            handleValidation={handleValidation(setFieldError)}
                                        />
                                    </>
                                }
                                {!isMobile &&
                                    <div className="flex flex-row justify-between items-start w-full md:w-2/3">
                                        <div className="w-3/4 pr-5">
                                            <InputField
                                                name="boravisteUlica"
                                                label={t("contact-address-page.address-field.label")}
                                                maxLength={35}
                                                defaultValue={values.boravisteUlica}
                                                error={t(errors.boravisteUlica as string)}
                                                handleValueChange={handleSetFieldValue(setFieldValue)}
                                                handleValidation={handleValidation(setFieldError)}
                                            />
                                        </div>

                                        <div className="w-3/4 pl-5">
                                            <InputField
                                                name="boravisteKucniBr"
                                                label={t("contact-address-page.house-number-field.label")}
                                                maxLength={16}
                                                defaultValue={values.boravisteKucniBr}
                                                error={t(errors.boravisteKucniBr as string)}
                                                handleValueChange={handleSetFieldValue(setFieldValue)}
                                                handleValidation={handleValidation(setFieldError)}
                                            />
                                        </div>
                                    </div>
                                }

                                {isMobile &&
                                    <>
                                        <InputField
                                            name="boravisteMjesto"
                                            label={t("contact-address-page.location-field.label")}
                                            maxLength={35}
                                            defaultValue={values.boravisteMjesto}
                                            error={t(errors.boravisteMjesto as string)}
                                            handleValueChange={handleSetFieldValue(setFieldValue)}
                                            handleValidation={handleValidation(setFieldError)}
                                        />
                                        <InputField
                                            name="boravistePostanskiBroj"
                                            label={t("contact-address-page.zip-code-field.label")}
                                            maxLength={16}
                                            defaultValue={values.boravistePostanskiBroj}
                                            error={t(errors.boravistePostanskiBroj as string)}
                                            handleValueChange={handleSetFieldValue(setFieldValue)}
                                            handleValidation={handleValidation(setFieldError)}
                                        />
                                    </>
                                }

                                {!isMobile &&
                                    <div className="flex flex-row justify-between items-start w-full md:w-2/3">
                                        <div className="w-3/4 pr-5">
                                            <InputField
                                                name="boravisteMjesto"
                                                label={t("contact-address-page.location-field.label")}
                                                maxLength={35}
                                                defaultValue={values.boravisteMjesto}
                                                error={t(errors.boravisteMjesto as string)}
                                                handleValueChange={handleSetFieldValue(setFieldValue)}
                                                handleValidation={handleValidation(setFieldError)}
                                            />
                                        </div>

                                        <div className="w-3/4 pl-5">
                                            <InputField
                                                name="boravistePostanskiBroj"
                                                label={t("contact-address-page.zip-code-field.label")}
                                                maxLength={16}
                                                defaultValue={values.boravistePostanskiBroj}
                                                error={t(errors.boravistePostanskiBroj as string)}
                                                handleValueChange={handleSetFieldValue(setFieldValue)}
                                                handleValidation={handleValidation(setFieldError)}
                                            />
                                        </div>
                                    </div>
                                }

                                <AutocompleteField
                                    className="md:w-2/3"
                                    name="boravisteDrzava"
                                    label={t("contact-address-page.country-field.label")}
                                    options={getCountryDataWithCroatiaBeingFirst(lng)}
                                    getOptionLabel={(option) => option?.drzava ?? ""}
                                    isOptionEqualToValue={(option, value) => option?.oznaka === value?.oznaka}
                                    defaultValue={values.boravisteDrzava}
                                    error={t(errors.boravisteDrzava as string)}
                                    handleValueChange={handleSetFieldValue(setFieldValue)}
                                    handleValidation={handleValidation(setFieldError)}
                                />

                                {isUkraine(values.boravisteDrzava) &&
                                    <AutocompleteField
                                        className="md:w-2/3"
                                        name="boravisteDrzavaRegija"
                                        label={t("common.ukraine-region.label")}
                                        options={getUkrajinaRegijaOptions(t)}
                                        getOptionLabel={(option) => option?.drzava || ""}
                                        isOptionEqualToValue={(option, value) => option?.oznaka === value?.oznaka}
                                        defaultValue={values.boravisteDrzavaRegija?.oznaka
                                            ? getUkrajinaRegijaById(values.boravisteDrzavaRegija.oznaka, t)
                                            : undefined}
                                        error={t(errors.boravisteDrzavaRegija as string)}
                                        handleValueChange={handleSetFieldValue(setFieldValue)}
                                        handleValidation={handleValidation(setFieldError)}
                                    />
                                }

                                <div className="flex flex-col">
                                    <Switch
                                        name="adresaZaDostavuDokumentacije"
                                        label={<span style={{ display: 'inline-flex', alignItems: 'center' }}>{t("contact-address-page.delivery-documentation-checkbox")} <Tooltip text={t("contact-address-page.delivery-documentation-tooltip")} className="cursor-help ml-1" iconSize="small" /></span>}
                                        defaultValue={values.adresaZaDostavuDokumentacije === DELIVERY_ADDRESS.BORAVISTE}
                                        handleValueChange={(checked) =>
                                            setFieldValue("adresaZaDostavuDokumentacije", checked ? DELIVERY_ADDRESS.BORAVISTE : DELIVERY_ADDRESS.PREBIVALISTE, false)}
                                    />
                                </div>
                            </>
                        }

                        <div className="flex flex-col">
                            <Switch
                                name="dostavaDrugaAdresa"
                                label={
                                    <span className="block max-w-[39rem]">
                                        {t("contact-address-page.second-address-switch")}
                                    </span>
                                }
                                defaultValue={values.dostavaDrugaAdresa}
                                handleValueChange={handleSetFieldValue(setFieldValue, setFieldError, values)}
                            />
                        </div>

                        {values.dostavaDrugaAdresa &&
                            <>
                                {isMobile &&
                                    <>
                                        <InputField
                                            name="dostavaUlica"
                                            label={t("contact-address-page.address-field.label")}
                                            maxLength={35}
                                            defaultValue={values.dostavaUlica}
                                            error={t(errors.dostavaUlica as string)}
                                            handleValueChange={handleSetFieldValue(setFieldValue)}
                                            handleValidation={handleValidation(setFieldError)}
                                        />

                                        <InputField
                                            name="dostavaKucniBr"
                                            label={t("contact-address-page.house-number-field.label")}
                                            maxLength={16}
                                            defaultValue={values.dostavaKucniBr}
                                            error={t(errors.dostavaKucniBr as string)}
                                            handleValueChange={handleSetFieldValue(setFieldValue)}
                                            handleValidation={handleValidation(setFieldError)}
                                        />
                                    </>
                                }
                                {!isMobile &&
                                    <div className="flex flex-row justify-between items-start w-full md:w-2/3">
                                        <div className="w-3/4 pr-5">
                                            <InputField
                                                name="dostavaUlica"
                                                label={t("contact-address-page.address-field.label")}
                                                maxLength={35}
                                                defaultValue={values.dostavaUlica}
                                                error={t(errors.dostavaUlica as string)}
                                                handleValueChange={handleSetFieldValue(setFieldValue)}
                                                handleValidation={handleValidation(setFieldError)}
                                            />
                                        </div>

                                        <div className="w-3/4 pl-5">
                                            <InputField
                                                name="dostavaKucniBr"
                                                label={t("contact-address-page.house-number-field.label")}
                                                maxLength={16}
                                                defaultValue={values.dostavaKucniBr}
                                                error={t(errors.dostavaKucniBr as string)}
                                                handleValueChange={handleSetFieldValue(setFieldValue)}
                                                handleValidation={handleValidation(setFieldError)}
                                            />
                                        </div>
                                    </div>
                                }

                                {isMobile &&
                                    <>
                                        <InputField
                                            name="dostavaMjesto"
                                            label={t("contact-address-page.location-field.label")}
                                            maxLength={35}
                                            defaultValue={values.dostavaMjesto}
                                            error={t(errors.dostavaMjesto as string)}
                                            handleValueChange={handleSetFieldValue(setFieldValue)}
                                            handleValidation={handleValidation(setFieldError)}
                                        />
                                        <InputField
                                            name="dostavaPostanskiBroj"
                                            label={t("contact-address-page.zip-code-field.label")}
                                            maxLength={16}
                                            defaultValue={values.dostavaPostanskiBroj}
                                            error={t(errors.dostavaPostanskiBroj as string)}
                                            handleValueChange={handleSetFieldValue(setFieldValue)}
                                            handleValidation={handleValidation(setFieldError)}
                                        />
                                    </>
                                }

                                {!isMobile &&
                                    <div className="flex flex-row justify-between items-start w-full md:w-2/3">
                                        <div className="w-3/4 pr-5">
                                            <InputField
                                                name="dostavaMjesto"
                                                label={t("contact-address-page.location-field.label")}
                                                maxLength={35}
                                                defaultValue={values.dostavaMjesto}
                                                error={t(errors.dostavaMjesto as string)}
                                                handleValueChange={handleSetFieldValue(setFieldValue)}
                                                handleValidation={handleValidation(setFieldError)}
                                            />
                                        </div>

                                        <div className="w-3/4 pl-5">
                                            <InputField
                                                name="dostavaPostanskiBroj"
                                                label={t("contact-address-page.zip-code-field.label")}
                                                maxLength={16}
                                                defaultValue={values.dostavaPostanskiBroj}
                                                error={t(errors.dostavaPostanskiBroj as string)}
                                                handleValueChange={handleSetFieldValue(setFieldValue)}
                                                handleValidation={handleValidation(setFieldError)}
                                            />
                                        </div>
                                    </div>
                                }

                                <AutocompleteField
                                    className="md:w-2/3"
                                    name="dostavaDrzava"
                                    label={t("contact-address-page.country-field.label")}
                                    options={getCountryDataWithCroatiaBeingFirst(lng)}
                                    getOptionLabel={(option) => option?.drzava ?? ""}
                                    isOptionEqualToValue={(option, value) => option?.oznaka === value?.oznaka}
                                    defaultValue={values.dostavaDrzava}
                                    error={t(errors.dostavaDrzava as string)}
                                    handleValueChange={handleSetFieldValue(setFieldValue)}
                                    handleValidation={handleValidation(setFieldError)}
                                />

                                {isUkraine(values.dostavaDrzava) &&
                                    <AutocompleteField
                                        className="md:w-2/3"
                                        name="dostavaDrzavaRegija"
                                        label={t("common.ukraine-region.label")}
                                        options={getUkrajinaRegijaOptions(t)}
                                        getOptionLabel={(option) => option?.drzava || ""}
                                        isOptionEqualToValue={(option, value) => option?.oznaka === value?.oznaka}
                                        defaultValue={values.dostavaDrzavaRegija?.oznaka
                                            ? getUkrajinaRegijaById(values.dostavaDrzavaRegija.oznaka, t)
                                            : undefined}
                                        error={t(errors.dostavaDrzavaRegija as string)}
                                        handleValueChange={handleSetFieldValue(setFieldValue)}
                                        handleValidation={handleValidation(setFieldError)}
                                    />
                                }

                                <div className="flex flex-col">
                                    <Switch
                                        name="adresaZaDostavuDokumentacije"
                                        label={<span style={{ display: 'inline-flex', alignItems: 'center' }}>{t("contact-address-page.delivery-documentation-checkbox")} <Tooltip text={t("contact-address-page.delivery-documentation-tooltip")} className="cursor-help ml-1" iconSize="small" /></span>}
                                        defaultValue={values.adresaZaDostavuDokumentacije === DELIVERY_ADDRESS.DRUGA}
                                        handleValueChange={(checked) =>
                                            setFieldValue("adresaZaDostavuDokumentacije", checked ? DELIVERY_ADDRESS.DRUGA : DELIVERY_ADDRESS.PREBIVALISTE, false)}
                                    />
                                </div>
                            </>
                        }

                        {!values.boraviste && values.dostavaDrugaAdresa && !values.trecaAdresa &&
                            <Button
                                className={isMobile ? "w-full" : "w-fit"}
                                type="button"
                                text={t("contact-address-page.add-address-button")}
                                img={<AddIcon fontSize="medium"/>}
                                onClick={() => setFieldValue("trecaAdresa", true, false)}
                            />
                        }

                        {!values.boraviste && values.dostavaDrugaAdresa && values.trecaAdresa &&
                            <>
                                {isMobile &&
                                    <>
                                        <InputField
                                            name="trecaUlica"
                                            label={t("contact-address-page.address-field.label")}
                                            maxLength={35}
                                            defaultValue={values.trecaUlica}
                                            error={t(errors.trecaUlica as string)}
                                            handleValueChange={handleSetFieldValue(setFieldValue)}
                                            handleValidation={handleValidation(setFieldError)}
                                        />

                                        <InputField
                                            name="trecaKucniBr"
                                            label={t("contact-address-page.house-number-field.label")}
                                            maxLength={16}
                                            defaultValue={values.trecaKucniBr}
                                            error={t(errors.trecaKucniBr as string)}
                                            handleValueChange={handleSetFieldValue(setFieldValue)}
                                            handleValidation={handleValidation(setFieldError)}
                                        />
                                    </>
                                }
                                {!isMobile &&
                                    <div className="flex flex-row justify-between items-start w-full md:w-2/3">
                                        <div className="w-3/4 pr-5">
                                            <InputField
                                                name="trecaUlica"
                                                label={t("contact-address-page.address-field.label")}
                                                maxLength={35}
                                                defaultValue={values.trecaUlica}
                                                error={t(errors.trecaUlica as string)}
                                                handleValueChange={handleSetFieldValue(setFieldValue)}
                                                handleValidation={handleValidation(setFieldError)}
                                            />
                                        </div>

                                        <div className="w-3/4 pl-5">
                                            <InputField
                                                name="trecaKucniBr"
                                                label={t("contact-address-page.house-number-field.label")}
                                                maxLength={16}
                                                defaultValue={values.trecaKucniBr}
                                                error={t(errors.trecaKucniBr as string)}
                                                handleValueChange={handleSetFieldValue(setFieldValue)}
                                                handleValidation={handleValidation(setFieldError)}
                                            />
                                        </div>
                                    </div>
                                }

                                {isMobile &&
                                    <>
                                        <InputField
                                            name="trecaMjesto"
                                            label={t("contact-address-page.location-field.label")}
                                            maxLength={35}
                                            defaultValue={values.trecaMjesto}
                                            error={t(errors.trecaMjesto as string)}
                                            handleValueChange={handleSetFieldValue(setFieldValue)}
                                            handleValidation={handleValidation(setFieldError)}
                                        />
                                        <InputField
                                            name="trecaPostanskiBroj"
                                            label={t("contact-address-page.zip-code-field.label")}
                                            maxLength={16}
                                            defaultValue={values.trecaPostanskiBroj}
                                            error={t(errors.trecaPostanskiBroj as string)}
                                            handleValueChange={handleSetFieldValue(setFieldValue)}
                                            handleValidation={handleValidation(setFieldError)}
                                        />
                                    </>
                                }

                                {!isMobile &&
                                    <div className="flex flex-row justify-between items-start w-full md:w-2/3">
                                        <div className="w-3/4 pr-5">
                                            <InputField
                                                name="trecaMjesto"
                                                label={t("contact-address-page.location-field.label")}
                                                maxLength={35}
                                                defaultValue={values.trecaMjesto}
                                                error={t(errors.trecaMjesto as string)}
                                                handleValueChange={handleSetFieldValue(setFieldValue)}
                                                handleValidation={handleValidation(setFieldError)}
                                            />
                                        </div>

                                        <div className="w-3/4 pl-5">
                                            <InputField
                                                name="trecaPostanskiBroj"
                                                label={t("contact-address-page.zip-code-field.label")}
                                                maxLength={16}
                                                defaultValue={values.trecaPostanskiBroj}
                                                error={t(errors.trecaPostanskiBroj as string)}
                                                handleValueChange={handleSetFieldValue(setFieldValue)}
                                                handleValidation={handleValidation(setFieldError)}
                                            />
                                        </div>
                                    </div>
                                }

                                <AutocompleteField
                                    className="md:w-2/3"
                                    name="trecaDrzava"
                                    label={t("contact-address-page.country-field.label")}
                                    options={getCountryDataWithCroatiaBeingFirst(lng)}
                                    getOptionLabel={(option) => option?.drzava ?? ""}
                                    isOptionEqualToValue={(option, value) => option?.oznaka === value?.oznaka}
                                    defaultValue={values.trecaDrzava}
                                    error={t(errors.trecaDrzava as string)}
                                    handleValueChange={handleSetFieldValue(setFieldValue)}
                                    handleValidation={handleValidation(setFieldError)}
                                />

                                {isUkraine(values.trecaDrzava) &&
                                    <AutocompleteField
                                        className="md:w-2/3"
                                        name="trecaDrzavaRegija"
                                        label={t("common.ukraine-region.label")}
                                        options={getUkrajinaRegijaOptions(t)}
                                        getOptionLabel={(option) => option?.drzava || ""}
                                        isOptionEqualToValue={(option, value) => option?.oznaka === value?.oznaka}
                                        defaultValue={values.trecaDrzavaRegija?.oznaka
                                            ? getUkrajinaRegijaById(values.trecaDrzavaRegija.oznaka, t)
                                            : undefined}
                                        error={t(errors.trecaDrzavaRegija as string)}
                                        handleValueChange={handleSetFieldValue(setFieldValue)}
                                        handleValidation={handleValidation(setFieldError)}
                                    />
                                }

                                <div className="flex flex-col">
                                    <Switch
                                        name="adresaZaDostavuDokumentacije"
                                        label={<span style={{ display: 'inline-flex', alignItems: 'center' }}>{t("contact-address-page.delivery-documentation-checkbox")} <Tooltip text={t("contact-address-page.delivery-documentation-tooltip")} className="cursor-help ml-1" iconSize="small" /></span>}
                                        defaultValue={values.adresaZaDostavuDokumentacije === DELIVERY_ADDRESS.TRECA}
                                        handleValueChange={(checked) =>
                                            setFieldValue("adresaZaDostavuDokumentacije", checked ? DELIVERY_ADDRESS.TRECA : DELIVERY_ADDRESS.PREBIVALISTE, false)}
                                    />
                                </div>

                                <Button
                                    type="button"
                                    variant="secondary"
                                    text={t("contact-address-page.remove-address-button")}
                                    img={<RemoveIcon fontSize="medium"/>}
                                    onClick={() => {
                                        clearTrecaAdresa(setFieldValue, setFieldError);
                                        if (values.adresaZaDostavuDokumentacije === DELIVERY_ADDRESS.TRECA) {
                                            setFieldValue("adresaZaDostavuDokumentacije", DELIVERY_ADDRESS.PREBIVALISTE, false);
                                        }
                                    }}
                                />
                            </>
                        }

                        <div className="!mt-5">
                            <div className="text-lg font-medium text-gray-700">
                                {t("contact-address-page.contact-info-title")}
                            </div>
                        </div>

                        {isMobile &&
                            <>
                                <AutocompleteField
                                    name="mobitelPredbroj"
                                    label={t("contact-address-page.country-code-field.label-required")}
                                    options={POZIVNI_BROJ_DATA}
                                    getOptionLabel={(option) => option?.oznaka || ""}
                                    isOptionEqualToValue={(option, value) => option?.oznaka === (value?.oznaka || value)}
                                    defaultValue={findPozivniBrojOption(values.mobitelPredbroj) as any}
                                    error={t(errors?.mobitelPredbroj as string)}
                                    handleValueChange={handleSetFieldValue(setFieldValue)}
                                    handleValidation={handleValidation(setFieldError)}
                                />

                                <InputField
                                    name="mobitel"
                                    label={t("contact-address-page.mobile-number-field.label")}
                                    maxLength={20}
                                    defaultValue={values.mobitel}
                                    error={t(errors?.mobitel as string)}
                                    handleValueChange={handleSetFieldValue(setFieldValue)}
                                    handleValidation={handleValidation(setFieldError)}
                                    info={t("contact-address-page.mobile-number-field.info")}
                                />
                            </>
                        }

                        {!isMobile &&
                            <div className="flex flex-row justify-between items-start w-full md:w-2/3">
                                <div className="w-3/4 pr-5">
                                    <AutocompleteField
                                        name="mobitelPredbroj"
                                        label={t("contact-address-page.country-code-field.label-required")}
                                        options={POZIVNI_BROJ_DATA}
                                        getOptionLabel={(option) => option?.oznaka || ""}
                                        isOptionEqualToValue={(option, value) => option?.oznaka === (value?.oznaka || value)}
                                        defaultValue={findPozivniBrojOption(values.mobitelPredbroj) as any}
                                        error={t(errors?.mobitelPredbroj as string)}
                                        handleValueChange={handleSetFieldValue(setFieldValue)}
                                        handleValidation={handleValidation(setFieldError)}
                                    />
                                </div>

                                <div className="w-3/4 pl-5">
                                    <InputField
                                        name="mobitel"
                                        label={t("contact-address-page.mobile-number-field.label")}
                                        maxLength={20}
                                        defaultValue={values.mobitel}
                                        error={t(errors?.mobitel as string)}
                                        handleValueChange={handleSetFieldValue(setFieldValue)}
                                        handleValidation={handleValidation(setFieldError)}
                                        info={t("contact-address-page.mobile-number-field.info")}
                                    />
                                </div>
                            </div>
                        }

                        {isMobile &&
                            <>
                                <AutocompleteField
                                    name="telefonPredbroj"
                                    label={t("contact-address-page.country-code-field.label")}
                                    options={POZIVNI_BROJ_DATA}
                                    getOptionLabel={(option) => option?.oznaka || ""}
                                    isOptionEqualToValue={(option, value) => option?.oznaka === (value?.oznaka || value)}
                                    defaultValue={findPozivniBrojOption(values.telefonPredbroj || "") as any}
                                    error={t(errors?.telefonPredbroj as string)}
                                    handleValueChange={handleSetFieldValue(setFieldValue)}
                                    handleValidation={handleValidation(setFieldError)}
                                />
                                <InputField
                                    name="telefon"
                                    label={t("contact-address-page.phone-number-field.label")}
                                    maxLength={20}
                                    defaultValue={values.telefon}
                                    error={t(errors?.telefon as string)}
                                    handleValueChange={handleSetFieldValue(setFieldValue)}
                                    handleValidation={handleValidation(setFieldError)}
                                    info={t("contact-address-page.phone-number-field.info")}
                                />
                            </>
                        }

                        {!isMobile &&
                            <div className="flex flex-row justify-between items-start w-full md:w-2/3">
                                <div className="w-3/4 pr-5">
                                    <AutocompleteField
                                        name="telefonPredbroj"
                                        label={t("contact-address-page.country-code-field.label")}
                                        options={POZIVNI_BROJ_DATA}
                                        getOptionLabel={(option) => option?.oznaka || ""}
                                        isOptionEqualToValue={(option, value) => option?.oznaka === (value?.oznaka || value)}
                                        defaultValue={findPozivniBrojOption(values.telefonPredbroj || "") as any}
                                        error={t(errors?.telefonPredbroj as string)}
                                        handleValueChange={handleSetFieldValue(setFieldValue)}
                                        handleValidation={handleValidation(setFieldError)}
                                    />
                                </div>
                                <div className="w-3/4 pl-5">
                                    <InputField
                                        name="telefon"
                                        label={t("contact-address-page.phone-number-field.label")}
                                        maxLength={20}
                                        defaultValue={values.telefon}
                                        error={t(errors?.telefon as string)}
                                        handleValueChange={handleSetFieldValue(setFieldValue)}
                                        handleValidation={handleValidation(setFieldError)}
                                        info={t("contact-address-page.phone-number-field.info")}
                                    />
                                </div>
                            </div>
                        }

                        <InputField
                            className="md:w-2/3"
                            name="mailAdresa"
                            label={t("contact-address-page.email-field.label")}
                            maxLength={50}
                            defaultValue={values.mailAdresa}
                            error={t(errors?.mailAdresa as string)}
                            handleValueChange={handleSetFieldValue(setFieldValue)}
                            handleValidation={handleValidation(setFieldError)}
                            info={t("contact-address-page.email-field.info")}
                        />

                    </div>

                    <div className="mt-10 text-center md:text-left">
                        <Button
                            onClick={() => handleBack(values, dirty)}
                            text={t("common.button-previous")}
                            variant="secondary"
                            disabled={false}
                            type="button"
                        />
                        <Button
                            type="submit"
                            text={t("common.button-next")}
                            disabled={isLoading}
                        />
                    </div>
                </form>
            )}
            </Formik>
        </Card>
    );
};

export default ContactAddressPage;